// InformeAplicaciones.js
// Pantalla SOLO LECTURA. No ejecuta INSERT/UPDATE/DELETE.
// Toda la data (autorización incluida) viene de la Edge Function informe-aplicaciones,
// autenticada con el JWT que emite el login legacy (familyGrowersAccessToken).
// Esta pantalla NO consulta Supabase directamente ni depende de Supabase Auth:
// la autorización real (ADMIN/PRODUCTOR) se resuelve server-side, nunca aquí.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  RefreshCw,
  X,
  ClipboardList,
  CheckCircle2,
  XCircle,
  FlaskConical,
  ShieldAlert,
  FileSpreadsheet,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Select } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { DataTable } from "./components/ui/data-table";
import { DateRangePicker } from "./components/ui/date-range-picker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogBody } from "./components/ui/dialog";
import { supabaseUrl } from "./lib/supabase";
import { toast } from "./lib/toast";
import { clearLegacySession } from "./lib/legacySession";

const EXPORT_ERROR_MESSAGES = {
  401: "Tu sesión para exportar el informe no es válida o expiró.",
  403: "No tienes permisos para exportar esta información.",
  413: null, // se usa el mensaje que devuelve la propia Edge Function
};

const INFORME_APLICACIONES_URL = `${supabaseUrl}/functions/v1/informe-aplicaciones`;
const TOKEN_KEY = "familyGrowersAccessToken";

const ESTADO_OPCIONES = [
  { value: "", label: "Todas" },
  { value: "ACTIVA", label: "Activas" },
  { value: "ANULADA", label: "Anuladas" },
];

const fmtFecha = (v) => (v ? dayjs(v).format("DD-MM-YYYY") : "—");
const orEmDash = (v) => (v == null || v === "" ? "—" : v);

const renderEstadoBadge = (estado) => (
  <Badge variant={estado === "ANULADA" ? "red" : "green"}>
    {estado === "ANULADA" ? "Anulada" : "Activa"}
  </Badge>
);

const renderProducto = (detalle) => {
  const list = Array.isArray(detalle) ? detalle : [];
  if (list.length === 0) return <span className="text-gray-400">—</span>;
  const first = list[0];
  return (
    <div>
      <div className="truncate">
        {first.nombrecomercial || "—"}
        {first.dosis ? <span className="text-gray-400"> · {first.dosis}</span> : null}
      </div>
      {list.length > 1 && (
        <div className="text-xs text-gray-400">
          +{list.length - 1} producto{list.length - 1 === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, className = "" }) => (
  <div className={className}>
    <div className="text-xs text-gray-400">{label}</div>
    <div className="font-medium text-gray-800 break-words">{value}</div>
  </div>
);

const KpiTile = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
    <div
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
      style={{ background: `${accent}1A`, color: accent }}
    >
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-xs text-gray-400 truncate">{label}</div>
      <div className="text-lg font-bold text-gray-900 leading-tight">{value}</div>
    </div>
  </div>
);

const AplicacionCard = ({ row, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left rounded-xl border border-gray-200 bg-white p-3 hover:bg-gray-50 transition-colors"
  >
    <div className="flex items-center justify-between gap-2 mb-1">
      <span className="text-sm font-semibold text-gray-900">{fmtFecha(row.fechaaplicacion)}</span>
      {renderEstadoBadge(row.estado)}
    </div>
    <div className="text-sm text-gray-700 mb-0.5">
      <span className="text-gray-400">Huerto: </span>
      {orEmDash(row.huerto)}
    </div>
    <div className="text-sm text-gray-700 mb-1">
      <span className="text-gray-400">Cuartel: </span>
      {orEmDash(row.cuartel)}
    </div>
    <div className="text-sm">{renderProducto(row.detalle)}</div>
  </button>
);

const AccesoDenegado = ({ icon: Icon = ShieldAlert, title, message, children }) => (
  <div className="max-w-md mx-auto bg-white rounded-xl border border-gray-200 shadow-sm p-6 mt-8 text-center">
    <Icon className="h-8 w-8 text-red-500 mx-auto mb-3" />
    <h2 className="text-lg font-bold text-gray-900 m-0 mb-1">{title}</h2>
    <p className="text-sm text-gray-500 mb-4">{message}</p>
    {children}
  </div>
);

const InformeAplicaciones = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [accessError, setAccessError] = useState(null); // "unauthorized" | "forbidden" | null
  const [errorMsg, setErrorMsg] = useState(null); // errores genéricos (500, red, etc.)

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(20);
  const [perfil, setPerfil] = useState(null);
  const [resumen, setResumen] = useState({ totalAplicaciones: 0, activas: 0, anuladas: 0, productosAplicados: 0 });
  const [productoresOptions, setProductoresOptions] = useState([{ value: 0, label: "Todos" }]);

  const [range, setRange] = useState([dayjs().subtract(30, "day"), dayjs()]);
  const [idProductor, setIdProductor] = useState(0);
  const [huertoText, setHuertoText] = useState("");
  const [cuartelText, setCuartelText] = useState("");
  const [estado, setEstado] = useState("ACTIVA");

  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [exportLoading, setExportLoading] = useState(false);

  const isAdmin = perfil?.rol === "ADMIN";

  // Sesión legacy antigua (isAuthenticated=true) creada antes de que existiera
  // familyGrowersAccessToken, o token perdido a mitad de sesión: se trata como
  // sesión incompleta y se manda al login principal — nunca un segundo login
  // ni el mensaje de "sin acceso al informe", que resultaría confuso aquí.
  const handleIncompleteSession = () => {
    clearLegacySession();
    toast.info("Tu sesión fue actualizada. Ingresa nuevamente para continuar.");
    navigate("/login", { replace: true });
  };

  // Filtros comunes a la vista paginada y a la exportación completa.
  const buildFilterParams = () => {
    const params = new URLSearchParams();
    if (range?.[0]) params.set("fechaDesde", dayjs(range[0]).format("YYYY-MM-DD"));
    if (range?.[1]) params.set("fechaHasta", dayjs(range[1]).format("YYYY-MM-DD"));
    if (isAdmin && idProductor) params.set("idproductor", String(idProductor));
    if (huertoText.trim()) params.set("huerto", huertoText.trim());
    if (cuartelText.trim()) params.set("cuartel", cuartelText.trim());
    if (estado) params.set("estado", estado);
    return params;
  };

  const fetchInforme = async (paginaArg) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      handleIncompleteSession();
      return;
    }

    setLoading(true);
    setErrorMsg(null);
    setAccessError(null);
    try {
      const params = buildFilterParams();
      params.set("pagina", String(paginaArg ?? pagina));
      params.set("limite", String(limite));

      const res = await fetch(`${INFORME_APLICACIONES_URL}?${params.toString()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const body = await res.json().catch(() => null);

      if (res.status === 401) {
        setAccessError("unauthorized");
        setItems([]);
        return;
      }
      if (res.status === 403) {
        setAccessError("forbidden");
        setItems([]);
        return;
      }
      if (!res.ok) {
        setErrorMsg("No fue posible cargar el informe. Intenta nuevamente.");
        setItems([]);
        return;
      }

      setItems(Array.isArray(body?.items) ? body.items : []);
      setTotal(Number(body?.total) || 0);
      setPagina(Number(body?.pagina) || 1);
      setLimite(Number(body?.limite) || 20);
      setPerfil(body?.perfil ?? null);
      setResumen(
        body?.resumen ?? { totalAplicaciones: 0, activas: 0, anuladas: 0, productosAplicados: 0 }
      );
      setProductoresOptions(
        [{ value: 0, label: "Todos" }].concat(
          (Array.isArray(body?.productores) ? body.productores : []).map((p) => ({
            value: p.id,
            label: p.nombre,
          }))
        )
      );
    } catch (e) {
      console.error(e);
      setErrorMsg("No fue posible cargar el informe. Intenta nuevamente.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInforme(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(
    () => [
      { title: "N°", dataIndex: "id", width: 70 },
      { title: "Fecha", dataIndex: "fechaaplicacion", width: 110, render: (v) => fmtFecha(v) },
      { title: "Productor", dataIndex: "productor", width: 200, ellipsis: true, render: (v) => orEmDash(v) },
      { title: "Huerto", dataIndex: "huerto", width: 150, ellipsis: true, render: (v) => orEmDash(v) },
      { title: "Cuartel", dataIndex: "cuartel", width: 150, ellipsis: true, render: (v) => orEmDash(v) },
      { title: "Producto / Dosis", key: "producto", width: 260, render: (_, r) => renderProducto(r.detalle) },
      { title: "Estado", dataIndex: "estado", width: 110, render: (v) => renderEstadoBadge(v) },
      {
        title: "",
        key: "actions",
        fixed: "right",
        width: 90,
        render: (_, r) => (
          <Button variant="link" size="sm" onClick={() => { setSelected(r); setDetailOpen(true); }}>
            Ver
          </Button>
        ),
      },
    ],
    []
  );

  const handleBuscar = () => fetchInforme(1);

  const handleLimpiar = () => {
    setRange([dayjs().subtract(30, "day"), dayjs()]);
    setIdProductor(0);
    setHuertoText("");
    setCuartelText("");
    setEstado("ACTIVA");
    setItems([]);
    setPagina(1);
  };

  const handleExportExcel = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      handleIncompleteSession();
      return;
    }

    setExportLoading(true);
    try {
      const params = buildFilterParams();
      params.set("exportar", "true");

      const res = await fetch(`${INFORME_APLICACIONES_URL}?${params.toString()}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });
      const body = await res.json().catch(() => null);

      if (!res.ok) {
        const msg =
          EXPORT_ERROR_MESSAGES[res.status] ??
          body?.error ??
          "No fue posible generar el archivo Excel. Intenta nuevamente.";
        toast.error(msg);
        return;
      }

      const rows = Array.isArray(body?.items) ? body.items : [];
      if (rows.length === 0) {
        toast.error("No existen registros para exportar con los filtros seleccionados.");
        return;
      }

      const XLSX = await import("xlsx");

      // Hoja 1: Aplicaciones — una fila por producto; si no hay detalle, una
      // fila igual con los campos de producto vacíos (no se pierde la aplicación).
      const aplicacionesRows = [];
      rows.forEach((r) => {
        const detalle = Array.isArray(r.detalle) && r.detalle.length > 0 ? r.detalle : [null];
        detalle.forEach((d) => {
          aplicacionesRows.push({
            "N° Aplicación": r.id,
            "Fecha Aplicación": fmtFecha(r.fechaaplicacion),
            "Productor": orEmDash(r.productor),
            "Huerto": orEmDash(r.huerto),
            "Cuartel": orEmDash(r.cuartel),
            "Producto": d?.nombrecomercial ?? "",
            "Ingrediente Activo": d?.ingredienteactivo ?? "",
            "Dosis": d?.dosis ?? "",
            "Mojamiento": d?.mojamiento ?? "",
            "Estado": r.estado === "ANULADA" ? "Anulada" : "Activa",
            "Origen": orEmDash(r.origen),
            "Fecha Anulación": r.fecha_anulacion ? fmtFecha(r.fecha_anulacion) : "",
            "Motivo Anulación": r.motivo_anulacion ?? "",
          });
        });
      });

      const wsAplicaciones = XLSX.utils.json_to_sheet(aplicacionesRows);
      const headers = Object.keys(aplicacionesRows[0]);
      wsAplicaciones["!cols"] = headers.map((h) => ({
        wch: Math.max(h.length, ...aplicacionesRows.map((row) => (row[h] ? String(row[h]).length : 0))) + 2,
      }));
      wsAplicaciones["!autofilter"] = { ref: `A1:${XLSX.utils.encode_col(headers.length - 1)}1` };

      // Hoja 2: Resumen
      const filtroTexto = (v) => (v && String(v).trim() !== "" ? v : "Todos");
      const productorSeleccionado = isAdmin
        ? productoresOptions.find((p) => p.value === idProductor)?.label
        : productoresOptions[1]?.label; // PRODUCTOR: su único productor
      const estadoLabel = ESTADO_OPCIONES.find((o) => o.value === estado)?.label || "Todas";

      const resumenRows = [
        ["Informe", "Informe de Aplicaciones"],
        ["Fecha generación", dayjs().format("DD-MM-YYYY HH:mm")],
        [],
        ["Filtros aplicados", ""],
        ["Fecha desde", range?.[0] ? dayjs(range[0]).format("DD-MM-YYYY") : "Todos"],
        ["Fecha hasta", range?.[1] ? dayjs(range[1]).format("DD-MM-YYYY") : "Todos"],
        ["Productor", filtroTexto(productorSeleccionado)],
        ["Huerto", filtroTexto(huertoText)],
        ["Cuartel", filtroTexto(cuartelText)],
        ["Estado", estadoLabel],
        [],
        ["KPIs", ""],
        ["Total aplicaciones", resumen.totalAplicaciones],
        ["Aplicaciones activas", resumen.activas],
        ["Aplicaciones anuladas", resumen.anuladas],
        ["Productos aplicados", resumen.productosAplicados],
      ];
      const wsResumen = XLSX.utils.aoa_to_sheet(resumenRows);
      wsResumen["!cols"] = [{ wch: 22 }, { wch: 30 }];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, wsAplicaciones, "Aplicaciones");
      XLSX.utils.book_append_sheet(wb, wsResumen, "Resumen");
      XLSX.writeFile(wb, `Informe_Aplicaciones_${dayjs().format("DD-MM-YYYY")}.xlsx`);
    } catch (e) {
      console.error(e);
      toast.error("No fue posible generar el archivo Excel. Intenta nuevamente.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleLogoutFromInforme = () => {
    clearLegacySession();
    window.location.href = "/login";
  };

  const totalPages = Math.max(1, Math.ceil(total / limite));
  const isEmpty = !loading && !errorMsg && !accessError && items.length === 0;

  if (accessError === "unauthorized") {
    return (
      <div className="bg-white p-4 rounded-xl">
        <AccesoDenegado
          title="Sesión no válida"
          message="Tu sesión para acceder al informe no es válida o expiró. Cierra sesión e ingresa nuevamente."
        >
          <div className="flex items-center justify-center gap-2">
            <Button variant="outline" onClick={() => navigate("/dashboard")}>Volver al Dashboard</Button>
            <Button variant="destructive" onClick={handleLogoutFromInforme}>Cerrar sesión</Button>
          </div>
        </AccesoDenegado>
      </div>
    );
  }

  if (accessError === "forbidden") {
    return (
      <div className="bg-white p-4 rounded-xl">
        <AccesoDenegado
          title="Sin acceso al informe"
          message="Tu usuario no tiene permisos para acceder al Informe de Aplicaciones."
        />
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-xl">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <h2 className="text-lg font-bold text-gray-900 m-0">Informe de Aplicaciones</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Consulta las aplicaciones registradas por los encargados de campo.
          </p>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2 my-4">
        <DateRangePicker value={range} onChange={(vals) => setRange(vals || [null, null])} format="YYYY-MM-DD" allowClear={false} />
        {isAdmin && (
          <Select
            options={productoresOptions}
            value={idProductor}
            onChange={(v) => setIdProductor(Number(v) || 0)}
            placeholder="Productor"
            style={{ width: 220 }}
            showSearch
            allowClear={false}
            optionFilterProp="label"
          />
        )}
        <Input placeholder="Huerto" value={huertoText} onChange={(e) => setHuertoText(e.target.value)} className="w-40" />
        <Input placeholder="Cuartel" value={cuartelText} onChange={(e) => setCuartelText(e.target.value)} className="w-40" />
        <Select
          value={estado}
          onChange={(v) => setEstado(v ?? "")}
          placeholder="Estado"
          style={{ width: 160 }}
          options={ESTADO_OPCIONES}
        />
        <Button onClick={handleBuscar} loading={loading}>
          <RefreshCw className="h-4 w-4" /> Buscar
        </Button>
        <Button variant="outline" onClick={handleLimpiar}>
          <X className="h-4 w-4" /> Limpiar filtros
        </Button>
        <Button variant="outline" onClick={handleExportExcel} loading={exportLoading} disabled={exportLoading}>
          <FileSpreadsheet className="h-4 w-4" />
          <span className="hidden sm:inline">{exportLoading ? "Generando Excel..." : "Exportar Excel"}</span>
          <span className="sm:hidden">Excel</span>
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiTile icon={ClipboardList} label="Total aplicaciones" value={resumen.totalAplicaciones} accent="#2563EB" />
        <KpiTile icon={CheckCircle2} label="Activas" value={resumen.activas} accent="#16A34A" />
        <KpiTile icon={XCircle} label="Anuladas" value={resumen.anuladas} accent="#DC2626" />
        <KpiTile icon={FlaskConical} label="Productos aplicados" value={resumen.productosAplicados} accent="#7C3AED" />
      </div>

      {/* Error genérico */}
      {errorMsg && !loading && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3 mb-4 flex items-center justify-between gap-3">
          <span>{errorMsg}</span>
          <Button variant="outline" size="sm" onClick={() => fetchInforme(pagina)}>Reintentar</Button>
        </div>
      )}

      {/* Empty */}
      {isEmpty && (
        <div className="text-gray-400 text-sm py-12 text-center">
          No se encontraron aplicaciones para los filtros seleccionados.
        </div>
      )}

      {/* Tabla desktop */}
      {!errorMsg && !isEmpty && (
        <div className="hidden md:block">
          <DataTable
            loading={loading}
            columns={columns}
            data={items}
            rowKey="id"
            bordered
            scroll={{ x: 1100 }}
            pagination={false}
          />
        </div>
      )}

      {/* Cards mobile */}
      {!errorMsg && !isEmpty && (
        <div className="md:hidden space-y-2">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-24 rounded-xl bg-gray-100 animate-pulse" />
              ))
            : items.map((r) => (
                <AplicacionCard key={r.id} row={r} onClick={() => { setSelected(r); setDetailOpen(true); }} />
              ))}
        </div>
      )}

      {/* Paginación (servidor) */}
      {!errorMsg && !isEmpty && total > limite && (
        <div className="flex items-center justify-between pt-3">
          <span className="text-sm text-gray-500">
            Página {pagina} de {totalPages} · {total} registros
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pagina <= 1 || loading}
              onClick={() => fetchInforme(pagina - 1)}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pagina >= totalPages || loading}
              onClick={() => fetchInforme(pagina + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Detalle */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Aplicación N° {selected?.id}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-4 max-h-[70vh] overflow-y-auto">
            {selected && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <Field label="Fecha" value={fmtFecha(selected.fechaaplicacion)} />
                  <Field label="Productor" value={orEmDash(selected.productor)} className="col-span-2 sm:col-span-1" />
                  <Field label="Huerto" value={orEmDash(selected.huerto)} />
                  <Field label="Cuartel" value={orEmDash(selected.cuartel)} />
                  <Field label="Origen" value={orEmDash(selected.origen)} />
                  <div>
                    <div className="text-xs text-gray-400">Estado</div>
                    <div className="mt-0.5">{renderEstadoBadge(selected.estado)}</div>
                  </div>
                </div>

                {selected.estado === "ANULADA" && (
                  <div className="rounded-lg bg-red-50 border border-red-100 p-3">
                    <div className="text-xs font-semibold text-red-700 mb-2">Anulación</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <Field label="Fecha anulación" value={fmtFecha(selected.fecha_anulacion)} />
                      {selected.motivo_anulacion && (
                        <Field label="Motivo" value={selected.motivo_anulacion} className="sm:col-span-2" />
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Productos</div>
                  {(!selected.detalle || selected.detalle.length === 0) ? (
                    <div className="text-gray-400 text-sm">Sin productos registrados.</div>
                  ) : (
                    <div className="space-y-2">
                      {selected.detalle.map((d, i) => (
                        <div key={i} className="rounded-lg border border-gray-100 bg-gray-50 p-3 grid grid-cols-2 gap-2 text-sm">
                          {d.nombrecomercial && <Field label="Nombre comercial" value={d.nombrecomercial} />}
                          {d.ingredienteactivo && <Field label="Ingrediente activo" value={d.ingredienteactivo} />}
                          {d.dosis && <Field label="Dosis" value={d.dosis} />}
                          {d.mojamiento && <Field label="Mojamiento" value={d.mojamiento} />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InformeAplicaciones;
